/**
 * Pricing calculation engine
 * Computes quote based on area, options, and ZIP-based location modifier
 */

export function calculateQuote(inputs, config) {
  const { area, thickness, reinforcement, finish, tint, cosmetics, zip } = inputs;
  
  // Get location factor first (will be applied to base, but hidden from breakdown)
  const locationFactor = getLocationFactor(zip, config);
  
  // Base price for 4-inch concrete (includes location factor)
  const base = area * config.baseRatePerSqFtFor4In * locationFactor;
  
  // Thickness adder
  const thicknessAdder = area * config.perInchAdder * (thickness - 4);
  
  // Reinforcement adder
  const reinforcementRate = config.reinforcementRatePerSqFt[reinforcement] || 0;
  const reinforcementAdder = area * reinforcementRate;
  
  // Finish adder
  const finishRate = config.finishRatePerSqFt[finish] || 0;
  const finishAdder = area * finishRate;
  
  // Tint adder
  const tintRate = config.tintRatePerSqFt[tint] || 0;
  const tintAdder = area * tintRate;
  
  // Cosmetic adders
  let cosmeticAdder = 0;
  const cosmeticBreakdown = [];
  
  Object.keys(cosmetics).forEach(key => {
    if (cosmetics[key] && config.cosmeticAdders[key]) {
      const adder = config.cosmeticAdders[key];
      const value = adder.type === 'perSqFt' 
        ? area * adder.value 
        : adder.value;
      cosmeticAdder += value;
      cosmeticBreakdown.push({
        label: adder.label,
        value
      });
    }
  });
  
  // Total before minimum (location factor already in base)
  const totalBeforeMinimum = base + thicknessAdder + reinforcementAdder + finishAdder + tintAdder + cosmeticAdder;
  
  // Apply minimum job charge
  const minimumApplied = totalBeforeMinimum < config.jobMinimum;
  const total = Math.max(totalBeforeMinimum, config.jobMinimum);
  
  // Calculate range if enabled
  let low = total;
  let high = total;
  let showRange = false;
  
  if (config.range?.enabled) {
    low = total * (1 - config.range.lowRangePct);
    high = total * (1 + config.range.highRangePct);
    showRange = true;
  }
  
  // Build breakdown (location factor hidden, included in base)
  const breakdown = [
    {
      label: `Base (${area.toFixed(0)} sq ft @ $${(config.baseRatePerSqFtFor4In * locationFactor).toFixed(2)}/sq ft)`,
      value: base
    }
  ];
  
  if (thicknessAdder > 0) {
    breakdown.push({
      label: `${thickness}" thickness upgrade`,
      value: thicknessAdder
    });
  }
  
  if (reinforcementAdder > 0) {
    breakdown.push({
      label: `${capitalizeOption(reinforcement)} reinforcement`,
      value: reinforcementAdder
    });
  }
  
  if (finishAdder > 0) {
    breakdown.push({
      label: `${capitalizeOption(finish)} finish`,
      value: finishAdder
    });
  }
  
  if (tintAdder > 0) {
    breakdown.push({
      label: `${capitalizeOption(tint)} color`,
      value: tintAdder
    });
  }
  
  cosmeticBreakdown.forEach(item => {
    breakdown.push(item);
  });
  
  if (minimumApplied) {
    breakdown.push({
      label: 'Minimum job charge',
      value: config.jobMinimum - totalBeforeMinimum
    });
  }
  
  return {
    total: Math.round(total),
    low: Math.round(low),
    high: Math.round(high),
    showRange,
    breakdown,
    minimumApplied,
    locationFactor,
    area
  };
}

function getLocationFactor(zip, config) {
  if (!zip || zip.length !== 5) {
    return config.zipDefaultModifier || 1.0;
  }
  
  return config.zipModifiers?.[zip] || config.zipDefaultModifier || 1.0;
}

function capitalizeOption(option) {
  if (!option) return '';
  
  const words = option.split(/[-_]/);
  return words.map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
}
